import {
  APIComponentAttributes,
  APIFieldType,
  APIOrganizationUser,
  ArdoqId,
} from '@ardoq/api-types';
import { toIds } from '@ardoq/core';
import { EnhancedScopeData } from '@ardoq/data-model';
import {
  enhancedScopeDataOperations,
  getDescendantIdsFromHierarchy,
  workspaceUtils,
} from '@ardoq/scope-data';
import {
  ardoqValidation,
  validate,
  validateAs,
  ValidationDescriptor,
} from '@ardoq/validation';
import { InventoryContext } from '../types';
import { TAGS_COLUMN } from '../inventoryConsts';

type ValidateComponentParams = {
  newValue: any;
  component: APIComponentAttributes;
  fieldName: string;
  context: InventoryContext;
};

export const validateComponent = ({
  newValue,
  component,
  fieldName,
  context,
}: ValidateComponentParams):
  | { isValid: true }
  | { isValid: false; errorMessages: string[] } => {
  const validationDescriptor = getValidationDescriptorForComponent(
    newValue,
    component,
    fieldName,
    context
  );
  return validate(validationDescriptor);
};

const getValidatorDescriptorForComponentType = (
  scopeData: EnhancedScopeData,
  newValue: string,
  component: APIComponentAttributes
): ValidationDescriptor => {
  const validComponentTypeIds =
    enhancedScopeDataOperations.getComponentTypeIdsByWorkspaceId(
      scopeData,
      component.rootWorkspace
    );
  return {
    value: newValue,
    validationSteps: [
      validateAs.includedIn({
        array: validComponentTypeIds,
      }),
    ],
  };
};

const getValidatorDescriptorForWorkspace = (
  scopeData: EnhancedScopeData,
  newValue: string
): ValidationDescriptor => ({
  value: newValue,
  validationSteps: [
    validateAs.includedIn({
      array: workspaceUtils.getAllWorkspaceIds(scopeData),
      message:
        'Workspace must use a valid workspace ID. Select from the list or paste workspace Ardoq OID.',
    }),
  ],
});

export const getInvalidParentIds = (
  scopeData: EnhancedScopeData,
  component: APIComponentAttributes
): ArdoqId[] => {
  const workspaceHierarchy = scopeData.hierarchy.get(component.rootWorkspace);
  const descendantIds = workspaceHierarchy
    ? getDescendantIdsFromHierarchy(workspaceHierarchy, component._id)
    : [];
  return [component._id, ...descendantIds];
};

/**
 * A parent must live in the same workspace as its child, and the API rejects
 * the edit if it doesn't. Scope data is a growing partial here, so an ID we
 * don't know is not necessarily invalid: only reject the ones we can see belong
 * to another workspace, and let the API judge the rest.
 */
const getParentIdsInOtherWorkspaces = (
  scopeData: EnhancedScopeData,
  newParentId: string | null,
  component: APIComponentAttributes
): ArdoqId[] => {
  if (!newParentId) {
    return [];
  }
  const newParent = enhancedScopeDataOperations.getComponent(
    scopeData,
    newParentId
  );
  if (!newParent || newParent.rootWorkspace === component.rootWorkspace) {
    return [];
  }
  return [newParentId];
};

const getValidatorDescriptorForParent = (
  scopeData: EnhancedScopeData,
  newParentId: string | null,
  component: APIComponentAttributes
): ValidationDescriptor => {
  const invalidParentIds = getInvalidParentIds(scopeData, component);

  return {
    value: newParentId,
    allowedValues: [null, undefined],
    validationSteps: [
      validateAs.ardoqId({
        message:
          'Parent must be a valid component ID. You can use the values from the Ardoq OID column.',
      }),
      validateAs.notIncludedIn({
        array: invalidParentIds,
        message: 'Cannot set component as its own parent or descendant.',
      }),
      validateAs.notIncludedIn({
        array: getParentIdsInOtherWorkspaces(scopeData, newParentId, component),
        message: 'Parent must be a component in the same workspace.',
      }),
    ],
  };
};

const getValidatorDescriptorForUserField = (
  users: APIOrganizationUser[],
  newValue: string
): ValidationDescriptor => {
  const userIds = toIds(users);
  return {
    value: newValue,
    allowedValues: [null, undefined],
    validationSteps: [
      validateAs.includedIn({
        array: userIds,
      }),
    ],
  };
};

const getValidationDescriptorForComponent = (
  newValue: any,
  component: APIComponentAttributes,
  fieldName: string,
  context: InventoryContext
): ValidationDescriptor => {
  if (!fieldName) {
    return { value: newValue, validationSteps: [] };
  }
  if (fieldName === 'name') {
    return getValidationDescriptorForComponentName(newValue);
  }

  const scopeData = context.scopeData;

  if (fieldName === 'rootWorkspace') {
    return getValidatorDescriptorForWorkspace(scopeData, newValue);
  }

  if (fieldName === 'type') {
    return getValidatorDescriptorForComponentType(
      scopeData,
      newValue,
      component
    );
  }

  if (fieldName === 'parent') {
    return getValidatorDescriptorForParent(scopeData, newValue, component);
  }

  if (fieldName === TAGS_COLUMN) {
    return ardoqValidation.getTagNamesValidatorDescriptor(newValue);
  }

  const fieldAttributes = enhancedScopeDataOperations.getFieldByName(
    scopeData,
    fieldName
  );
  if (!fieldAttributes) {
    return { value: newValue, validationSteps: [] };
  }

  // fieldAttributes don't contain user list, so we can't use ardoqValidation.getValidatorDescriptorByField for USER type
  if (fieldAttributes.type === APIFieldType.USER) {
    return getValidatorDescriptorForUserField(context.users, newValue);
  }

  return ardoqValidation.getValidatorDescriptorByField({
    value: newValue,
    field: fieldAttributes,
  });
};

const getValidationDescriptorForComponentName = (
  newValue: string
): ValidationDescriptor => {
  return {
    value: newValue,
    validationSteps: [
      validateAs.requiredString({ message: 'Name is required.' }),
      validateAs.minLength({ length: 1, message: '' }),
    ],
  };
};

export type NewComponentFieldValidationError = {
  columnId: string;
  errorMessage: string;
};

const isFilled = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '';

// Identity columns are validated explicitly because the column field differs from the
// attribute for the component type ('type' column ↔ 'typeId' attribute) and because the
// name/rootWorkspace/parent validators need their dedicated descriptors.
// `columnId` matches the AG Grid column field used as the `_cellErrors` key.
const getIdentityFields = (
  component: APIComponentAttributes
): { columnId: string; value: unknown }[] => [
  { columnId: 'name', value: component.name },
  { columnId: 'rootWorkspace', value: component.rootWorkspace },
  { columnId: 'type', value: component.typeId },
  { columnId: 'parent', value: component.parent },
];

const IDENTITY_ATTRIBUTES = new Set([
  'name',
  'rootWorkspace',
  'type',
  'typeId',
  'parent',
]);

/**
 * Returns the remaining filled attributes that map to an actual field (custom fields and
 * typed default fields). System attributes (`_id`, `model`, `created`, `ardoq`, …) and
 * reference columns are excluded automatically because they don't resolve via
 * `getFieldByName`. The attribute key equals both the column field (the `_cellErrors` key)
 * and the `fieldName` expected by `validateComponent`.
 */
const getOtherFilledFields = (
  component: APIComponentAttributes,
  context: InventoryContext
): { columnId: string; value: unknown }[] =>
  Object.keys(component)
    .filter(key => !IDENTITY_ATTRIBUTES.has(key))
    .map(key => ({
      columnId: key,
      value: (component as Record<string, unknown>)[key],
    }))
    .filter(
      ({ columnId, value }) =>
        isFilled(value) &&
        Boolean(
          enhancedScopeDataOperations.getFieldByName(
            context.scopeData,
            columnId
          )
        )
    );

// The tags value lives on the row under the `_tags` column field rather than a `getFieldByName`
// field, so it is collected explicitly. `columnId` matches the `_cellErrors` key for the column.
const getTagsField = (
  component: APIComponentAttributes
): { columnId: string; value: unknown }[] => {
  const value = (component as Record<string, unknown>)[TAGS_COLUMN];
  return isFilled(value) ? [{ columnId: TAGS_COLUMN, value }] : [];
};

/**
 * Validates the whole component row before it is sent to the backend, rather than just the
 * cell that changed.
 *
 * A multi-cell paste (e.g. an entire Excel row) is processed cell-by-cell. By the time a
 * valid `name` cell triggers the save, the row data may already contain invalid values from
 * the same paste (a workspace/type name instead of an ID, an invalid parent ID, a malformed
 * field value, …). Validating only the changed cell would let those reach the backend and
 * produce a 400. This validates every filled field so the save can be blocked and the
 * offending cells flagged.
 */
export const validateNewComponent = ({
  component,
  context,
}: {
  component: APIComponentAttributes;
  context: InventoryContext;
}): NewComponentFieldValidationError[] =>
  [
    ...getIdentityFields(component),
    ...getTagsField(component),
    ...getOtherFilledFields(component, context),
  ].flatMap(({ columnId, value }) => {
    const result = validateComponent({
      newValue: value,
      component,
      fieldName: columnId,
      context,
    });
    return result.isValid
      ? []
      : [{ columnId, errorMessage: result.errorMessages.join(' ') }];
  });
